import React, { useState, useRef } from "react";
import {
  Scale,
  MapPin,
  Award,
  Users,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import { motion } from "framer-motion";

const About = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {});
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const stats = [
    { label: "Especializado em Servidores Públicos", icon: Scale },
    { label: "Atendemos todo o Brasil", icon: MapPin },
    { label: "+10 anos de Experiência", icon: Award },
    { label: "+3.000 clientes", icon: Users },
  ];

  return (
    <section
      id="sobre"
      className="py-24 bg-background overflow-hidden pt-9 pb-9"
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="w-[85%] md:w-[70%] lg:w-[42%] relative group mx-auto shrink-0"
          >
            <div
              className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white cursor-pointer bg-black/90 aspect-video flex items-center justify-center group"
              onClick={togglePlay}
            >
              <video
                ref={videoRef}
                src="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6abeef9817f6f30939bf8bb8.mp4"
                poster="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a30a307d64da5ce6b7c3c23.webp"
                className="w-full h-full object-cover"
                loop
                playsInline
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />

              {/* Play Overlay */}
              {!isPlaying && (
                <div className="absolute inset-0 bg-black/35 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3 transition-opacity duration-300">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent text-primary flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform duration-300 pl-1 ring-4 ring-white/30">
                    <Play className="w-8 h-8 md:w-10 md:h-10 fill-primary" />
                  </div>
                  <span className="text-white text-xs md:text-sm font-semibold tracking-wider uppercase drop-shadow-md bg-black/40 px-3 py-1 rounded-full">
                    Assistir ao vídeo
                  </span>
                </div>
              )}

              {/* Floating controls when playing */}
              {isPlaying && (
                <div className="absolute bottom-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={toggleMute}
                    aria-label={isMuted ? "Ativar som" : "Desativar som"}
                    className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                  >
                    {isMuted ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                    aria-label="Pausar"
                    className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                  >
                    <Pause className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-accent/20 rounded-full blur-3xl -z-0" />
          </motion.div>

          <div className="lg:w-1/2 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              <h2 className="text-3xl md:text-[48px] lg:text-[48px] font-bold text-primary leading-[1.1] font-serif">
                Conhecemos a sua realidade por dentro.
              </h2>
              <div className="w-24 h-1.5 bg-accent rounded-full" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="space-y-6 text-muted-foreground text-xl leading-relaxed"
            >
              <p className="font-semibold text-primary/80 italic border-l-4 border-accent pl-6 pr-0 lg:pr-20 text-base">
                Nossa trajetória profissional começou após trabalharmos por
                décadas atuando como Servidores Públicos, vivenciando a
                realidade da administração pública e seus efeitos na vida
                funcional e financeira do servidor.
              </p>
              <p className="pr-0 lg:pr-[120px] text-base">
                Essa vivência prática orienta o nosso trabalho há mais de 10
                anos. Defendemos mais de 3.000 clientes com um único objetivo:
                garantir todos os direitos da nossa classe.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="w-full rounded-2xl overflow-hidden shadow-lg my-8 block md:hidden"
            >
              <img
                src="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a30a307d64da5ce6b7c3c23.webp"
                alt="Equipe Pinheiro Advocacia"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-muted/30 border border-border/50 hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="bg-accent/10 p-2 rounded-full group-hover:scale-110 group-hover:bg-accent/20 transition-all duration-300">
                    <stat.icon className="w-6 h-6 text-accent" />
                  </div>
                  <span className="font-bold text-primary text-lg transition-colors duration-300">
                    {stat.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
